import { RequestUtil } from '../../../../common/util/request-util';
import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { lastValueFrom } from 'rxjs';
import ObjectUtil from '../../../../common/util/object-util';
import { GraphQLQueryResponse } from '../../../../common/interfaces/graphql-query-response';
import { SearchParam } from '../../../../common/interfaces/search-param';
import { SortParams } from '../../../../common/interfaces/sort-params';
import { ColumnFilter } from '../../../../common/interfaces/column-filter';
import { StationAppBaseBackendService } from '../station-app-base-backend.service';
import { ServiceErrorUtil } from '../../../../common/util/service-error.util';

@Injectable({
  providedIn: 'root',
})
export class NotificationLogService extends StationAppBaseBackendService {
  override async get(
    afterCursor: string | null = null,
    searchParams: SearchParam[][] | null = [],
    sortParams: SortParams | null = null,
    columnFilters?: ColumnFilter[] | null,
    otherParams: any | null = null,
  ): Promise<GraphQLQueryResponse> {
    const requestId = RequestUtil.generateRequestId();
    const query = gql`
      query (
        $first: Int
        $after: String
        $last: Int
        $before: String
        $where: NotificationLogFilterInput
        $order: [NotificationLogSortInput!]
      ) {
        notificationLogs(first: $first, last: $last, after: $after, before: $before, where: $where, order: $order) {
          totalCount
          pageInfo {
            startCursor
            hasPreviousPage
            hasNextPage
            endCursor
          }
          nodes {
            id
            priority
            source
            message
            createdAtUTC
          }
        }
      }
    `;

    const queryResponse = await lastValueFrom(
      this.apollo.query<any>({
        variables: {
          first: otherParams?.first ?? 10,
          after: afterCursor ?? null,
          last: null,
          before: null,
          where: this.buildWhere(searchParams, columnFilters),
          order: this.buildOrder(sortParams),
        },
        query,
        context: {
          headers: {
            'App-Id': this.appState.getAppInstanceId(),
            'Operator-Id': this.appState.getOperatorId(),
            'Request-Id': requestId,
            'PermissionSet-Id': this.appState.getPermissionSetId(),
          },
        },
      }),
    );

    if (!queryResponse.data?.notificationLogs) {
      throw ServiceErrorUtil.createServiceError(
        this.constructor.name,
        'Error getting notification logs',
        requestId,
      );
    }

    const notificationLogs = ObjectUtil.deepCopy(
      queryResponse.data.notificationLogs.nodes,
    );

    return {
      nextPageLink: queryResponse.data.notificationLogs.pageInfo.endCursor ?? null,
      startPageLink: queryResponse.data.notificationLogs.pageInfo.startCursor ?? null,
      endPageLink: queryResponse.data.notificationLogs.pageInfo.endCursor ?? null,
      hasPreviousPage: queryResponse.data.notificationLogs.pageInfo.hasPreviousPage ?? false,
      hasNextPage: queryResponse.data.notificationLogs.pageInfo.hasNextPage ?? false,
      totalCount: queryResponse.data.notificationLogs.totalCount,
      data: notificationLogs,
    };
  }

  override getById(id: number): Promise<any> {
    throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Method not implemented.');
  }

  override create(data: any): Promise<any> {
    throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Method not implemented.');
  }

  override update(data: any): Promise<any> {
    throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Method not implemented.');
  }

  override delete(id: number): Promise<any> {
    throw ServiceErrorUtil.createServiceError(this.constructor.name, 'Method not implemented.');
  }

  override async getValidationData(): Promise<any> {
    return Promise.resolve({});
  }
}
